import { yupResolver } from '@hookform/resolvers/yup';
import {
  FormControlLabel,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useOrganicRegistrationForm } from '.';
import { useEmailConfirmationMutation } from '../../../../api/registration/organic/emailConfirmation';
import { usePolicySearchMutation } from '../../../../api/registration/organic/policySearch';
import CheckFillIcon from '../../../../assets/icons/CheckMark';
import Snackbar from '../../../../components/Snackbar';
import ControlledTextField from '../../../../components/TextField/ControlledTextField';
import Button from '../../../../components/styledComponents/Button';
import { InfoButton } from '../../../../components/styledComponents/IconButtons';
import { LinkWithNoRouter } from '../../../../components/styledComponents/Link';
import colors from '../../../../constants/colors';
import { useDirectAccessAnalytics } from '../../../../hooks/useAnalytics';
import { useSnackbarsContext } from '../../../../store/SnackbarProvider';
import DateOfBirthTextField from '../../Components/DateOfBirthTextField';
import ClubRedirectDialog from '../../Dialogs/ClubRedirectDialog';
import PolicyNumberHelper from '../../Dialogs/PolicyNumberHelper';
import {
  OrganicRegistrationFormValues,
  organicPolicyDetailsValidationSchema,
} from '../schemas/organicRegistration';

const PolicyDetailsForm = () => {
  const [openPolicyHelperDialog, setOpenPolicyHelperDialog] = useState(false);
  const [showClubRedirectDialog, setShowClubRedirectDialog] = useState(false);
  const [autoRadioIsFocused, setAutoRadioIsFocused] = useState<boolean>(false);
  const [homeRadioIsFocused, setHomeRadioIsFocused] = useState<boolean>(false);
  const [loginUrl, setLoginUrl] = useState('');
  const { formValues, setFormValues } = useOrganicRegistrationForm();
  const policySearchMutation = usePolicySearchMutation();
  const sendEmailVerificationMutation = useEmailConfirmationMutation();
  const {
    control,
    formState: { errors, isValid },
    resetField,
    getValues,
    watch,
    reset,
  } = useForm<OrganicRegistrationFormValues>({
    resolver: yupResolver(organicPolicyDetailsValidationSchema),
    mode: 'onChange',
    defaultValues: {
      policyType: null,
    },
  });
  const navigate = useNavigate();
  const { track } = useDirectAccessAnalytics();
  const { state, enqueueSnackbar, closeSnackbar } = useSnackbarsContext();

  const policyType = watch('policyType');

  const trackValidationErrors = (fieldName?: string) => {
    if (fieldName) {
      if (fieldName in errors) {
        track('Input Validated', {
          event_type: 'Automated System Process',
          location: 'Feature Card',
          registration_type: 'Organic',
          field_name: fieldName,
          error_message:
            errors[fieldName as keyof OrganicRegistrationFormValues]!.message,
          status: 'Failed',
        });
      }
    } else {
      for (const errorField in errors) {
        track('Input Validated', {
          event_type: 'Automated System Process',
          location: 'Feature Card',
          registration_type: 'Organic',
          field_name: errorField,
          error_message:
            errors[errorField as keyof OrganicRegistrationFormValues]!.message,
          status: 'Failed',
        });
      }
    }
  };

  const trackInputBlurred = (fieldName?: string) => {
    const values = getValues();
    if (fieldName && values[fieldName as keyof OrganicRegistrationFormValues]) {
      track('Field Information Entered', {
        event_type: 'User Information Entered',
        location: 'Feature Card',
        registration_type: 'Organic',
        field_name: fieldName,
        input: values[fieldName as keyof OrganicRegistrationFormValues]!,
      });
    }
  };

  const inputBlurred = (fieldName?: string) => {
    trackValidationErrors(fieldName);
    trackInputBlurred(fieldName);
  };

  useEffect(() => {
    if (policyType) {
      track('Verification Method Selected', {
        event_type: 'Option Selected',
        location: 'Feature Card',
        registration_type: 'Organic',
        flow: policyType === 'auto' ? 'Auto' : 'Property',
        email: formValues.email,
        login_method: 'DA',
      });
    }
  }, [policyType]);

  useEffect(() => {
    const { email, password } = formValues;
    if (!email || !password) {
      navigate('/register');
    }
  }, []);

  const handleFinishClick = async () => {
    closeSnackbar();
    const values = getValues();

    const policySearchRequestBody = {
      userName: formValues.email,
      password: formValues.password,
      searchType: values.policyType as 'auto' | 'home',
      searchParams:
        values.policyType === 'home'
          ? {
              policyNumber: values.policyNumber,
              dob: values.dateOfBirth,
              zipCode: values.zipCode,
            }
          : {
              policyLast4: values.policyNumber,
              driverLicense: values.driversLicense,
              zipCode: values.zipCode,
            },
    };

    try {
      const policySearchResponse = await policySearchMutation.mutateAsync(
        policySearchRequestBody
      );
      setFormValues({
        ...formValues,
        ...values,
        firstName: policySearchResponse.firstName,
      });
      await sendEmailVerificationMutation.mutateAsync({
        email: formValues.email,
        firstName: policySearchResponse.firstName,
      });
      track('Registration Completed', {
        event_type: 'Automated System Process',
        location: 'Feature Card',
        registration_type: 'Organic',
        flow: policyType
          ? policyType === 'auto'
            ? 'Auto'
            : 'Property'
          : 'Email',
        login_method: 'DA',
      });
      navigate('/register/verification');
    } catch (err) {
      reset();
      const response = (err as AxiosError)?.response;
      track('Registration Failed', {
        event_type: 'Automated System Process',
        location: 'Feature Card',
        registration_type: 'Organic',
        flow: policyType
          ? policyType === 'auto'
            ? 'Auto'
            : 'Property'
          : 'Email',
        reason:
          // @ts-expect-error - Axios error data type
          response?.data?.message ||
          'Policy search or Send email verification failed',
        login_method: 'DA',
      });

      if (response?.status === 409) {
        // user already exists
        track('User Messaged', {
          event_type: 'Messaged',
          location: 'Feature Card',
          message_type: 'Error',
          contents: 'You have a MyPolicy account',
        });
        // @ts-expect-error - Axios error data type
        if (response?.data?.loginURL) {
          setShowClubRedirectDialog(true);
          // @ts-expect-error - Axios error data type
          setLoginUrl(response?.data?.loginURL);
        } else {
          enqueueSnackbar({
            variant: 'error',
            title: 'You have a MyPolicy account',
            key: 'policy-search-user-already-registered',
            content: 'Sign in with your username and password',
          });
          navigate('/login');
        }
      } else {
        track('User Messaged', {
          event_type: 'Messaged',
          location: 'Feature Card',
          message_type: 'Error',
          contents: 'We are unable to process your request right now.',
        });
        enqueueSnackbar({
          key: 'policy-search-organic-registration-error',
          variant: 'error',
          title: 'We are unable to process your request right now.',
          content: (
            <>
              Please check the details and try again, or contact Service at {}
              <LinkWithNoRouter
                href="tel:+888.980.5650"
                underline="none"
                sx={{
                  fontSize: {
                    mobile: 16,
                    tablet: 18,
                  },
                  fontWeight: 400,
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  color: colors.csaaSpectrumBlue50,
                }}
              >
                888.980.5650
              </LinkWithNoRouter>
              .
            </>
          ),
        });
      }
      window.scrollTo(0, 0);
    }
  };

  useEffect(
    () => () => {
      closeSnackbar('policy-search-organic-registration-error');
    },
    []
  );

  return (
    <Stack
      direction="column"
      alignItems="center"
      sx={{
        px: { mobile: 2, tablet: 4 },
        backgroundColor: colors.csaaGray,
        pt: 3,
      }}
      data-testid="organic-registration-2"
    >
      <Stack>
        <Stack
          justifyContent="center"
          sx={{
            py: 1.5,
            width: 1,
          }}
          spacing={1.5}
        >
          {state.snackbars.length > 0 &&
            state.snackbars.map((snack, idx) => (
              <Snackbar
                key={snack.key}
                {...snack}
                id={snack.key!}
                dataTestId={`${snack.key}-${idx}`}
              />
            ))}
        </Stack>
        <Typography
          variant="h1"
          sx={{
            fontWeight: 500,
            fontSize: { mobile: 33, tablet: 44 },
            lineHeight: { mobile: '48px', desktop: '56px' },
          }}
        >
          Choose one policy to create your account
        </Typography>
        <Stack
          direction="column"
          sx={{
            background: colors.csaaWhite,
            borderRadius: 2,
            p: { mobile: 2, tablet: 5 },
            mt: { tablet: 3 },
            mb: { tablet: 25 },
          }}
        >
          <Typography
            sx={{
              fontWeight: 400,
              fontFamily: 'Cabin',
              fontSize: { mobile: 16, tablet: 18 },
              lineHeight: '24px',
            }}
          >
            Policies including auto, property and personal umbrella will be
            available in your new MyPolicy account. You may link any missing
            policies after your account is created.
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontFamily: 'Cabin',
              fontSize: { mobile: 16, tablet: 18 },
              lineHeight: '24px',
              pt: 2,
            }}
          >
            You&apos;ll need
          </Typography>
          <List sx={{ pt: 2, pb: { mobile: 3, tablet: 5 } }}>
            <ListItem disablePadding>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <CheckFillIcon titleAccess="Required Item Checkmark" />
              </ListItemIcon>
              <Typography
                sx={{
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  fontSize: { mobile: 16, tablet: 18 },
                  fontWeight: 400,
                  color: colors.csaaDark,
                }}
              >
                Policy number
              </Typography>
            </ListItem>
            <ListItem disablePadding>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <CheckFillIcon titleAccess="Required Item Checkmark" />
              </ListItemIcon>
              <Typography
                sx={{
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  fontSize: { mobile: 16, tablet: 18 },
                  fontWeight: 400,
                  color: colors.csaaDark,
                }}
              >
                Driver&apos;s license number (auto only)
              </Typography>
            </ListItem>
          </List>
          <Controller
            name="policyType"
            control={control}
            render={({ field: { onBlur, onChange, value } }) => (
              <RadioGroup
                name="policyType"
                aria-label="Policy Type"
                value={value}
                onChange={(e) => {
                  resetField('policyNumber');
                  resetField('zipCode');
                  resetField('driversLicense');
                  resetField('dateOfBirth');
                  resetField('policyType');
                  onChange(e);
                  closeSnackbar();
                }}
                onBlur={onBlur}
              >
                <FormControlLabel
                  control={
                    <Radio
                      onFocus={() => setAutoRadioIsFocused(true)}
                      onBlur={() => setAutoRadioIsFocused(false)}
                      disableRipple
                      size="small"
                      sx={{
                        color: colors.csaaMid,
                        '&.Mui-checked': {
                          color: colors.csaaMid,
                        },
                        '& .MuiSvgIcon-root': {
                          height: 12,
                          width: 12,
                        },
                        ...(autoRadioIsFocused && {
                          '& > span': {
                            borderRadius: '50%',
                            outline: `2px solid ${colors.csaaBlack}`,
                            outlineOffset: 2,
                          },
                        }),
                        '&:hover': {
                          backgroundColor: colors.csaaOverlayBlack,
                        },
                      }}
                      inputProps={{
                        'aria-label': 'Auto',
                        'data-testid': 'organic-auto-radio-input',
                      }}
                    />
                  }
                  value="auto"
                  label="Auto"
                  disableTypography
                  sx={{
                    mt: 2,
                    mx: 0,
                    fontSize: 18,
                    fontWeight: { tablet: 700 },
                    width: 'fit-content',
                  }}
                />
                {policyType === 'auto' && (
                  <Stack direction="column">
                    <ControlledTextField
                      name="policyNumber"
                      control={control}
                      label="Policy number (last 4 digits)"
                      preventWhitespace
                      preventNonNumericValues
                      sx={{ mt: 2 }}
                      handleBlur={(e, onBlur) => {
                        inputBlurred('policyNumber');
                        return onBlur(e);
                      }}
                      inputProps={{
                        maxLength: 4,
                        pattern: '[0-9]*',
                        'data-testid': 'organic-auto-policy-number-input',
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <InfoButton
                              aria-label="Open policy number info dialog"
                              onClick={() => {
                                track('Info Bubble Clicked', {
                                  event_type: 'UX Modified',
                                  location: 'Feature Card',
                                  registration_type: 'Organic',
                                  flow: 'Email',
                                  link: 'policyNumber',
                                });
                                setOpenPolicyHelperDialog(true);
                              }}
                            />
                          </InputAdornment>
                        ),
                        required: true,
                      }}
                    />
                    <ControlledTextField
                      name="driversLicense"
                      control={control}
                      className="csaa-qm-block"
                      label="Drivers license number"
                      helperText="Enter all letters and numbers from primary named insured's license."
                      sx={{ mt: 2 }}
                      preventWhitespace
                      preventNonAlphaNumericValues
                      handleBlur={(e, onBlur) => {
                        inputBlurred('driversLicense');
                        return onBlur(e);
                      }}
                      InputProps={{ required: true }}
                      inputProps={{
                        'data-testid': 'organic-auto-drivers-license-input',
                      }}
                    />
                    <ControlledTextField
                      name="zipCode"
                      control={control}
                      label="Zip code"
                      helperText="Use the property or garaging address, not the mailing address."
                      sx={{ mt: 2 }}
                      preventWhitespace
                      preventNonNumericValues
                      handleBlur={(e, onBlur) => {
                        inputBlurred('zipCode');
                        return onBlur(e);
                      }}
                      InputProps={{ required: true }}
                      inputProps={{
                        autoComplete: 'postal-code',
                        maxLength: 5,
                        pattern: '[0-9]*',
                        'data-testid': 'organic-auto-zip-code-input',
                      }}
                    />
                  </Stack>
                )}
                <FormControlLabel
                  control={
                    <Radio
                      onFocus={() => setHomeRadioIsFocused(true)}
                      onBlur={() => setHomeRadioIsFocused(false)}
                      disableRipple
                      size="small"
                      sx={{
                        color: colors.csaaMid,
                        '&.Mui-checked': {
                          color: colors.csaaMid,
                        },
                        '& .MuiSvgIcon-root': {
                          height: 12,
                          width: 12,
                        },
                        ...(homeRadioIsFocused && {
                          '& > span': {
                            borderRadius: '50%',
                            outline: `2px solid ${colors.csaaBlack}`,
                            outlineOffset: 2,
                          },
                        }),
                        '&:hover': {
                          backgroundColor: colors.csaaOverlayBlack,
                        },
                      }}
                      inputProps={{
                        'aria-label': 'Home',
                        'data-testid': 'organic-home-radio-input',
                      }}
                    />
                  }
                  value="home"
                  label="Property"
                  disableTypography
                  sx={{
                    mt: 2,
                    mx: 0,
                    fontSize: 18,
                    fontWeight: { tablet: 700 },
                    width: 'fit-content',
                  }}
                />
              </RadioGroup>
            )}
          />
          {policyType === 'home' && (
            <Stack direction="column">
              <ControlledTextField
                name="policyNumber"
                control={control}
                label="Policy number"
                preventWhitespace
                preventNonAlphaNumericValues
                sx={{ mt: 2 }}
                handleBlur={(e, onBlur) => {
                  inputBlurred('policyNumber');
                  return onBlur(e);
                }}
                inputProps={{
                  'data-testid': 'organic-home-policy-number-input',
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <InfoButton
                        aria-label="Open policy number info dialog"
                        onClick={() => {
                          track('Info Bubble Clicked', {
                            event_type: 'UX Modified',
                            location: 'Feature Card',
                            registration_type: 'Organic',
                            flow: 'Email',
                            link: 'policyNumber',
                          });
                          setOpenPolicyHelperDialog(true);
                        }}
                      />
                    </InputAdornment>
                  ),
                  required: true,
                }}
              />
              <DateOfBirthTextField
                name="dateOfBirth"
                control={control}
                className="csaa-qm-block"
                label="Date of Birth"
                helperText="Enter the policyholder's date of birth."
                sx={{ mt: 2 }}
                preventWhitespace
                handleBlur={(e, onBlur) => {
                  inputBlurred('dateOfBirth');
                  return onBlur(e);
                }}
                inputProps={{
                  maxLength: 10,
                  'data-testid': 'organic-home-dob-input',
                }}
                showVisibilityToggle
              />
              <ControlledTextField
                name="zipCode"
                control={control}
                label="Zip code"
                helperText="Use the property or garaging address, not the mailing address."
                sx={{ mt: 2 }}
                preventWhitespace
                preventNonNumericValues
                handleBlur={(e, onBlur) => {
                  inputBlurred('zipCode');
                  return onBlur(e);
                }}
                inputProps={{
                  autoComplete: 'postal-code',
                  maxLength: 5,
                  'data-testid': 'organic-home-zip-code-input',
                }}
                InputProps={{ required: true }}
              />
            </Stack>
          )}
          <Stack
            direction="row"
            justifyContent="flex-end"
            sx={{ mt: { mobile: 3, tablet: 5 }, width: 1 }}
          >
            <Button
              disabled={!isValid}
              role="link"
              onClick={handleFinishClick}
              data-testid="organic-registration-finish-button"
              loading={
                policySearchMutation.isPending ||
                sendEmailVerificationMutation.isPending
              }
            >
              Finish
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <PolicyNumberHelper
        open={openPolicyHelperDialog}
        closeDialog={() => setOpenPolicyHelperDialog(false)}
      />
      <ClubRedirectDialog
        open={showClubRedirectDialog}
        closeDialog={() => {
          setShowClubRedirectDialog(false);
        }}
        loginUrl={loginUrl}
        loginRedirect
      />
    </Stack>
  );
};

export default PolicyDetailsForm;
